import { captureException } from '@sentry/nextjs';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';

import client from '@/apollo-client';
import { EventListItemProps } from '@/components/Event/EventListItem';
import Layout from '@/components/Layout';
import ArtistDetail, {
  ArtistDetailProps,
} from '@/components/Pages/ArtistDetail';
import {
  ArtistDocument,
  ArtistQuery,
  ArtistQueryVariables,
} from '@/generated/graphql';

export default function ArtistPage(props: {
  artist: NonNullable<ArtistQuery['performer']>;
}) {
  const { artist } = props;

  const artistDetailProps: ArtistDetailProps = {
    imageUrl: artist.imgUrl,
    name: artist.name,
    bio: artist.description,
    websiteUrl: artist.websiteUrl,
    instagramUrl: artist.instagramUrl,
    facebookUrl: artist.facebookUrl,
    spotifyUrl: artist.spotifyUrl,
    events: artist.events.edges.map<EventListItemProps>((event) => ({
      venueName: event.node.venue.name,
      title: event.node.title,
      itinerary: formatInTimeZone(
        event.node.startDateTime,
        event.node.venue.timezone,
        `EEE MMM dd, hh:mmaa zzz`, // Sun Apr 10, 10:00PM EST
        { locale: enUS },
      ),
      imageUrl: event.node.imgUrl,
      id: event.node.id,
      cancelled: event.node.isCancelled,
    })),
  };

  return (
    <Layout headerType={`float`}>
      <NextSeo
        title={`${artist.name} | Someday`}
        description={`Get tickets for upcoming events by ${artist.name}.`}
        openGraph={{
          title: `${artist.name} | Someday`,
          description: `Get tickets for upcoming events by ${artist.name}.`,
          images: [
            {
              url: artist.imgUrl,
            },
          ],
        }}
      />
      <ArtistDetail {...artistDetailProps} />
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const { query } = context;
    const { id } = query;

    if (typeof id !== `string`) {
      return {
        notFound: true,
      };
    }

    const { data } = await client.query<ArtistQuery, ArtistQueryVariables>({
      query: ArtistDocument,
      variables: { id: id, datetimeGt: Number(new Date()) },
    });

    if (!data.performer) {
      return {
        notFound: true,
      };
    }

    return {
      props: { artist: data.performer },
    };
  } catch (error) {
    captureException(error);

    return {
      redirect: {
        destination: `/500`,
        permanent: false,
      },
    };
  }
};
